import { OTHER_NODES_LABEL } from "@faire/help-center-self-serve/shared/other-node";
import { useArticles } from "@faire/help-center-utils/hooks/article/useArticles";
import {
  useAssignSettingSelfServeBrandInstantAnswers,
  useSettingSelfServeBrandInstantAnswers,
} from "@faire/help-center-utils/settings/useSettingSelfServiceBrandInstantAnswers";
import { useStrictLocalization } from "@faire/web--source/common/localization";
import { Storefront } from "@faire/web--source/slate/icons/foundation/Storefront";
import React from "react";

import { SelfServeNode, SelfServeNodes } from "../../types";

export const useMyShopBrandNodes = (): SelfServeNodes => {
  const { strictLocalize } = useStrictLocalization();
  const enableSettingSelfServeBrandInstantAnswers =
    useSettingSelfServeBrandInstantAnswers();
  const assignSettingSelfServeBrandInstantAnswers =
    useAssignSettingSelfServeBrandInstantAnswers();
  const articles = useArticles();

  const commonFields: SelfServeNode = {
    id: "MyShop",
    label: strictLocalize({
      defaultMessage: "Manage shop",
      description: {
        text: "A top level issue node in the full page self-serve experience of Faire's Help Center",
        img: "https://cdn.faire.com/fastly/2779e7c0038efabd6c1a4096ffaf88367c36676aae315d6d3de6054daf474fb2.png",
      },
    }),
    icon: <Storefront fontSize="24px" color="faireBlack" />,
    level: 1,
    assignSetting: assignSettingSelfServeBrandInstantAnswers,
    sidebarTitle: strictLocalize({
      defaultMessage: "Shop issue",
      description: {
        text: "Title for displaying the issue on the order summary sidebar",
      },
    }),
  };

  if (!enableSettingSelfServeBrandInstantAnswers) {
    return {
      MyShop: {
        ...commonFields,
        children: ["Settings", "OtherMyShop"],
      },
      Settings: {
        id: "Settings",
        label: strictLocalize({
          defaultMessage: "Settings",
          description: {
            text: "A second level issue node in the full page self-serve experience of Faire's Help Center",
          },
        }),
        level: 2,
      },
      OtherMyShop: {
        id: "OtherMyShop",
        label: OTHER_NODES_LABEL(strictLocalize),
        articles: [
          articles.OptimizingMyPage.PRODUCT_PHOTOGRAPHY_GUIDELINES,
          articles.OptimizingMyPage.BRAND_STORY_GUIDELINES,
          articles.OptimizingMyPage.ADD_VIDEO,
        ],
        level: 2,
      },
    };
  }

  return {
    MyShop: {
      ...commonFields,
      children: ["Analytics", "CustomerReviews", "ShopPage", "ShopPreferences"],
      shouldStopAddingDefaultOtherNodeToChildren: true,
    },
    Analytics: {
      id: "Analytics",
      label: strictLocalize({
        defaultMessage: "Analytics",
        description: {
          text: "A second level issue node in the full page self-serve experience of Faire's Help Center",
        },
      }),
      children: ["AboutAnalytics", "CustomerServiceAnalytics"],
      level: 2,
    },
    CustomerReviews: {
      id: "CustomerReviews",
      label: strictLocalize({
        defaultMessage: "Customer reviews",
        description: {
          text: "A second level issue node in the full page self-serve experience of Faire's Help Center",
        },
      }),
      children: ["RequestReview", "DisputeReview"],
      level: 2,
    },
    ShopPage: {
      id: "ShopPage",
      label: strictLocalize({
        defaultMessage: "Shop page",
        description: {
          text: "A second level issue node in the full page self-serve experience of Faire's Help Center",
        },
      }),
      children: ["ShopImagery", "ShopAboutSection"],
      level: 2,
    },
    ShopPreferences: {
      id: "ShopPreferences",
      label: strictLocalize({
        defaultMessage: "Shop preferences",
        description: {
          text: "A second level issue node in the full page self-serve experience of Faire's Help Center",
        },
      }),
      children: [
        "OrderMinimums",
        "InternationalOrderSettings",
        "ScheduledOrders",
        "ShopLeadTime",
        "OnLineOnlyRetailers",
        "PauseShop",
      ],
      level: 2,
    },
    AboutAnalytics: {
      id: "AboutAnalytics",
      label: strictLocalize({
        defaultMessage: "About analytics",
        description: {
          text: "A third level issue node in the full page self-serve experience of Faire's Help Center",
        },
      }),
      level: 3,
    },
    CustomerServiceAnalytics: {
      id: "CustomerServiceAnalytics",
      label: strictLocalize({
        defaultMessage: "Customer service analytics",
        description: {
          text: "A third level issue node in the full page self-serve experience of Faire's Help Center",
        },
      }),
      level: 3,
    },
    RequestReview: {
      id: "RequestReview",
      label: strictLocalize({
        defaultMessage: "Request a review",
        description: {
          text: "A third level issue node in the full page self-serve experience of Faire's Help Center",
        },
      }),
      level: 3,
    },
    DisputeReview: {
      id: "DisputeReview",
      label: strictLocalize({
        defaultMessage: "Dispute a review",
        description: {
          text: "A third level issue node in the full page self-serve experience of Faire's Help Center",
        },
      }),
      level: 3,
    },
    ShopImagery: {
      id: "ShopImagery",
      label: strictLocalize({
        defaultMessage: "Shop imagery",
        description: {
          text: "A third level issue node in the full page self-serve experience of Faire's Help Center",
        },
      }),
      level: 3,
    },
    ShopAboutSection: {
      id: "ShopAboutSection",
      label: strictLocalize({
        defaultMessage: "Shop about section",
        description: {
          text: "A third level issue node in the full page self-serve experience of Faire's Help Center",
        },
      }),
      level: 3,
    },
    OrderMinimums: {
      id: "OrderMinimums",
      label: strictLocalize({
        defaultMessage: "Order minimums",
        description: {
          text: "A third level issue node in the full page self-serve experience of Faire's Help Center",
        },
      }),
      level: 3,
    },
    InternationalOrderSettings: {
      id: "InternationalOrderSettings",
      label: strictLocalize({
        defaultMessage: "International order settings",
        description: {
          text: "A third level issue node in the full page self-serve experience of Faire's Help Center",
        },
      }),
      level: 3,
    },
    ScheduledOrders: {
      id: "ScheduledOrders",
      label: strictLocalize({
        defaultMessage: "Scheduled orders",
        description: {
          text: "A third level issue node in the full page self-serve experience of Faire's Help Center",
        },
      }),
      level: 3,
    },
    ShopLeadTime: {
      id: "ShopLeadTime",
      label: strictLocalize({
        defaultMessage: "Shop lead time",
        description: {
          text: "A third level issue node in the full page self-serve experience of Faire's Help Center",
        },
      }),
      level: 3,
    },
    OnLineOnlyRetailers: {
      id: "OnLineOnlyRetailers",
      label: strictLocalize({
        defaultMessage: "Online-only retailers",
        description: {
          text: "A third level issue node in the full page self-serve experience of Faire's Help Center",
        },
      }),
      level: 3,
    },
    PauseShop: {
      id: "PauseShop",
      label: strictLocalize({
        defaultMessage: "Pause shop",
        description: {
          text: "A third level issue node in the full page self-serve experience of Faire's Help Center",
        },
      }),
      level: 3,
    },
  };
};
